import { AxiosResponse } from 'axios';

import {
  runGetApiRequest, runPostApiRequest, runDeleteApiRequest, runUpdateApiRequest,
} from 'services/Api';

import {
  DELETE_POS_QUEUE, POS_QUEUE_ROUTE, POS_CREATE_CUSTOMER_ROUTE, POS_ORDER_DETAILS_ROUTE, ADD_CUSTOMER_TO_QUEUE,
  INVENTORY_SEARCH_ITEM_ROUTE,
  POS_ADD_ITEM_TO_ORDER,
  POS_GET_DISCOUNTS,
  POS_APPLY_DISCOUNT,
} from 'constants/apiRoutes';
import { NewPOSCustomer } from 'types/customer.interface';
import { InventoryItemSearchResponseData, InventorySearchPayload } from 'types/inventory.interface';
import {
  POSAddItemToOrderPayload, POSDiscount, POSOrderDetails, POSQueue,
} from 'types/pos.interface';
import { formatRoute } from 'utils/formatters';
import { getClientDetailedInfo } from 'utils/storageUtils';
import { NewCustomerFormData } from 'validation/newCustomerSchema';

export const getPOSQueues = (): Promise<AxiosResponse<POSQueue[]>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runGetApiRequest({
    endpoint: formatRoute(POS_QUEUE_ROUTE, { clientId }),
    params: {},
  });
};

export const createPOSCustomer = (
  customer: NewCustomerFormData,
): Promise<AxiosResponse<NewPOSCustomer>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runPostApiRequest(
    formatRoute(POS_CREATE_CUSTOMER_ROUTE, { clientId }),
    customer,
  );
};

export const deletePOSQueue = (queueId: number): Promise<AxiosResponse<void>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runDeleteApiRequest(formatRoute(DELETE_POS_QUEUE, { clientId, queueId }));
};

export const getPOSOrderDetails = (
  orderId: string,
): Promise<AxiosResponse<POSOrderDetails>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runGetApiRequest({
    endpoint: formatRoute(POS_ORDER_DETAILS_ROUTE, { clientId, orderId }),
    params: {},
  });
};

export const addCustomerToQueue = (customerId: string): Promise<AxiosResponse<void>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runPostApiRequest(formatRoute(ADD_CUSTOMER_TO_QUEUE, { clientId, customerId }));
};

export const updatePOSOrder = (
  orderId: string,
  order: Partial<POSOrderDetails>,
  clientId: string,
): Promise<AxiosResponse<POSOrderDetails>> => runUpdateApiRequest(
  formatRoute(POS_ORDER_DETAILS_ROUTE, { clientId, orderId }),
  order,
);

export const getProducts = (
  orderId: string,
  body: InventorySearchPayload,
  clientId: string,
): Promise<AxiosResponse<InventoryItemSearchResponseData>> => runPostApiRequest(
  formatRoute(INVENTORY_SEARCH_ITEM_ROUTE, { clientId, orderId }),
  body,
);

export const addItemToOrder = (
  orderId: string,
  clientId: string,
  body: POSAddItemToOrderPayload,
): Promise<AxiosResponse<POSOrderDetails>> => runPostApiRequest(
  formatRoute(POS_ADD_ITEM_TO_ORDER, { clientId, orderId }),
  body,
);

export const getPOSDiscounts = (): Promise<AxiosResponse<POSDiscount[]>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runGetApiRequest({
    endpoint: formatRoute(POS_GET_DISCOUNTS, { clientId }),
    params: {},
  });
};

export const applyPOSDiscount = (discountId: number, orderId: string): Promise<AxiosResponse<void>> => {
  const clientId = getClientDetailedInfo()?.clientInfo?.id;
  return runPostApiRequest(formatRoute(POS_APPLY_DISCOUNT, { clientId, orderId, discountId }));
};
